import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';
import { DictionaryService } from '../../services/dictionaryService';
import { Pronunciation } from '../Entry';
import { SaveButton } from '../WordActions';
import Reveal from '../Reveal';

const RELATED_LIMIT = 8;

const getEarlierDays = () =>
  [1, 2, 3].map((daysAgo) => {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return {
      label: date.toLocaleDateString('en-US', { weekday: 'long' }),
      word: DictionaryService.getWordForDate(date),
    };
  });

// Similar words straight from the dictionary entry
const getRelatedWords = (entry) => {
  const words = entry.groups.flatMap((g) => [...g.senses.flatMap((s) => s.synonyms), ...g.synonyms]);
  return [...new Set(words)].filter((w) => w !== entry.word).slice(0, RELATED_LIMIT);
};

const WordOfTheDayFeature = () => {
  const [wordOfTheDay, setWordOfTheDay] = useState(null);
  const [earlierDays] = useState(getEarlierDays);
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  useEffect(() => {
    DictionaryService.getWordOfTheDay().then(setWordOfTheDay);
  }, []);

  const entry = wordOfTheDay?.entry;
  const group = entry?.groups[0];
  const sense = group?.senses[0];
  const example = entry?.groups.flatMap((g) => g.senses).find((s) => s.example)?.example;
  const related = entry ? getRelatedWords(entry) : [];

  return (
    <section aria-labelledby="wotd-heading" className="wrap py-16 md:py-24">
      <Reveal className="grid gap-5 lg:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)]">
        <article className="relative flex flex-col overflow-hidden rounded-[2rem] bg-lavender p-7 sm:p-10 md:p-12">
          <div aria-hidden="true" className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-surface/40 dark:hidden" />

          <div className="relative flex flex-wrap items-center justify-between gap-3">
            <p id="wotd-heading" className="inline-flex items-center gap-2 rounded-full bg-surface/75 px-4 py-1.5 text-sm font-semibold text-ink">
              <CalendarDays size={16} aria-hidden="true" />
              Word of the day
            </p>
            <p className="text-[0.95rem] text-ink-soft">{today}</p>
          </div>

          {wordOfTheDay ? (
            <div className="relative flex flex-1 flex-col">
              <h2 className="mt-8 break-words font-display text-[clamp(3rem,7vw,5.5rem)] leading-none tracking-[-0.02em] text-ink">
                {wordOfTheDay.word}
              </h2>

              <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-3">
                {group && (
                  <span className="rounded-full bg-surface/75 px-3 py-1 font-serif italic text-ink">{group.partOfSpeech}</span>
                )}
                {entry && <Pronunciation entry={entry} />}
              </div>

              {sense ? (
                <p className="mt-7 max-w-2xl text-xl leading-relaxed text-ink sm:text-2xl">{sense.definition}</p>
              ) : (
                <p className="mt-7 max-w-2xl text-lg text-ink-soft">
                  The definition didn’t load here. The full entry may still open.
                </p>
              )}

              {example && (
                <blockquote className="mt-5 max-w-2xl border-l-4 border-ink/20 pl-4 font-serif text-lg italic leading-relaxed text-ink-soft">
                  “{example}”
                </blockquote>
              )}

              <div className="mt-auto flex flex-wrap items-center gap-3 pt-9">
                <Link
                  to={`/define/${wordOfTheDay.word}`}
                  className="inline-flex h-12 items-center rounded-full bg-ink px-6 font-semibold text-paper transition-[background-color,transform] hover:bg-ink/85 active:scale-[0.97]"
                >
                  Read the full definition
                </Link>
                <SaveButton word={wordOfTheDay.word} className="h-12 px-5" />
              </div>
            </div>
          ) : (
            <div className="mt-8 space-y-5 motion-safe:animate-pulse" aria-hidden="true">
              <div className="h-20 w-72 max-w-full rounded-2xl bg-surface/60" />
              <div className="h-6 w-full max-w-lg rounded-full bg-surface/60" />
              <div className="h-6 w-2/3 max-w-md rounded-full bg-surface/60" />
            </div>
          )}
        </article>

        <div className="flex flex-col gap-5">
          {related.length > 0 && (
            <div className="rounded-[2rem] bg-mint p-7 sm:p-8">
              <h3 className="font-display text-2xl text-ink">Words like {wordOfTheDay.word}</h3>
              <p className="mt-1 text-[0.95rem] text-ink-soft">Similar words listed in its dictionary entry.</p>
              <ul className="mt-5 flex flex-wrap gap-2">
                {related.map((word) => (
                  <li key={word}>
                    <Link
                      to={`/define/${encodeURIComponent(word)}`}
                      className="inline-block rounded-full bg-surface/80 px-3.5 py-1.5 text-[0.95rem] text-ink transition-[background-color,transform] hover:bg-surface active:scale-95"
                    >
                      {word}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex-1 rounded-[2rem] border border-rule bg-surface p-7 sm:p-8">
            <h3 className="font-display text-2xl text-ink">Earlier this week</h3>
            <ul className="mt-4 divide-y divide-rule">
              {earlierDays.map(({ label, word }) => (
                <li key={label}>
                  <Link
                    to={`/define/${word}`}
                    className="-mx-3 flex items-baseline justify-between gap-4 rounded-xl px-3 py-3 transition-colors hover:bg-butter/60"
                  >
                    <span className="font-display text-xl text-ink">{word}</span>
                    <span className="text-sm text-ink-soft">{label}</span>
                  </Link>
                </li>
              ))}
            </ul>
            <Link to="/word-of-the-day" className="mt-4 inline-block text-[0.95rem] text-link">
              See this week’s words
            </Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
};

export default WordOfTheDayFeature;
